/*
 * Fleet-admin i18n — a self-owned `fleet` namespace registered onto the SAME
 * i18next singleton base-ui configures (i18next is a module singleton; base-ui's
 * initI18n() sets it up, we just add our bundle). So every fleet string is gated
 * behind the active language exactly like the rest of the app, and ONLY strings
 * that come from the backend non-deterministically (gateway names, org names, IPs,
 * versions, raw error text) stay un-translated.
 *
 * Usage: `ft('btn.unclaim')`, `ft('panel.coordsVal', { lat, lng })`. Components that
 * render translated text add `new LocaleController(this)` so they re-render when the
 * language switches.
 */
import i18next from 'i18next';
import type { ReactiveController, ReactiveControllerHost } from 'lit';
import { onLocaleChange } from '@vitesse/base-ui/i18n';

// ── en is the SOURCE; the other locales mirror its shape exactly ──
const en = {
  tree: { overview: 'Overview', releases: 'Releases', map: 'Map', alerts: 'Alerts', rollouts: 'Rollouts', people: 'Permissions' },
  people: {
    pick: 'Select a member to view or change what they may do.',
    noMembers: 'No members in this organization yet.',
    filter: 'Filter permissions…',
    save: 'Save',
    saving: 'Saving…',
    // The two "empty" states read differently on purpose: one defers to the role,
    // the other overrules it.
    defaultAllowed: 'Default — allowed',
    defaultDenied: 'Default — admin only',
    all: 'All surfaces',
    none: 'Revoked',
    hintMemberDefault: 'Members hold this unless revoked',
    hintAdminOnly: 'Admins only unless granted',
    rowLabel: 'Permission {{key}}',
  },
  alerts: {
    critical: '{{n}} critical',
    warnings: '{{n}} warnings',
    nTotal: '{{n}} events',
  },
  tab: { overview: 'Overview', hardware: 'Hardware', network: 'Network', software: 'Software', security: 'Security' },
  ids: {
    notRunning: 'IDS not running on this relay',
    loss: 'Capture loss',
    threat: 'Threat',
    alerts: 'Alerts',
    severity: 'Crit · High · Med',
    rules: 'Rules',
    blocks: 'Blocks',
    topSignatures: 'Top signatures',
    topSources: 'Top sources',
    protocols: 'Protocols',
    activeBlocks: 'Active blocks',
    engineUp: 'Engine up',
    engineDown: 'Engine down',
    mode: { off: 'Off', monitor: 'Monitor', protect: 'Protect' },
  },
  server: {
    resources: 'Resources',
    cpu: 'CPU',
    memory: 'Memory',
    disk: 'Disk',
    restricted: 'Stats are restricted to Vitesse admins',
    location: 'Location',
    uptime: 'Uptime',
    load: 'Load avg',
    processes: 'Processes',
    model: 'CPU',
    cores: 'Cores',
    os: 'OS',
    kernel: 'Kernel',
    arch: 'Architecture',
    memTotal: 'Memory',
    diskTotal: 'Disk',
  },
  m: {
    apiSuccess: 'API success',
    activeConns: 'Active conns',
    power: 'Power',
    diskUsed: 'Disk used',
    diskFree: 'Disk free',
    latency: 'Latency',
    egress: 'Egress',
    errorRate: 'Error rate',
    release: 'Release',
    attacks: 'Attacks',
    dataPoints: 'Data points',
    packets: 'Packets lost',
    cloudLatency: 'Cloud latency',
    brokerLoad: 'Edge broker',
    threads: 'CPU free',
    ips: 'Msg/s',
    disk: 'Disk',
    updateStatus: 'Update',
    itMac: 'IT MAC',
    downtime: 'Downtime',
    ingress: 'Ingress',
    traffic: 'Traffic',
    cloudBroker: 'Broker',
    flows: 'Flows',
    sessions: 'Sessions',
    pktsPerSec: 'Pkts/s',
    idsCpu: 'IDS CPU',
    idsMem: 'IDS mem',
    memUsed: 'Memory used',
    serverType: 'Type',
  },
  btn: {
    unclaim: 'Unclaim',
    revoke: 'Revoke',
    remove: 'Remove',
    rename: 'Rename',
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    retry: 'retry',
    claim: 'Claim',
  },
  hint: {
    unclaim: 'Release this relay from its org',
    revoke: 'Kill-switch — void credentials, block reconnect',
    remove: 'Permanently erase from the registry',
    rename: 'Rename this relay',
    refresh: 'Refresh the fleet',
    claim: 'Claim this relay by entering the code it displayed on enrollment',
  },
  status: { online: 'Online', offline: 'Offline', unknown: 'Unknown', claimed: 'Claimed', unclaimed: 'Unclaimed', revoked: 'Revoked' },
  panel: {
    liveHealth: 'Live health',
    quickFacts: 'Quick facts',
    utilization: 'Utilization',
    identifiers: 'Identifiers',
    addresses: 'Addresses',
    versions: 'Versions',
    ownerOrg: 'Owner org',
    location: 'Location',
    coordinates: 'Coordinates',
    lastSeen: 'Last seen',
    firstSeen: 'First seen',
    connection: 'Connection',
    tier: 'Tier',
    uptime: 'Uptime',
    publicIp: 'Public IP',
    itIp: 'IT IP',
    otIp: 'OT IP',
    wifiIp: 'Wi-Fi IP',
    mac: 'MAC address',
    otMac: 'OT MAC address',
    gatewayId: 'Relay id',
    hostname: 'Hostname',
    nodeKind: 'Node kind',
    claimStatus: 'Claim status',
    backend: 'Backend',
    platform: 'Platform',
    updater: 'Updater',
    docker: 'Docker',
    os: 'OS',
    unnamed: 'Unnamed relay',
    namePlaceholder: 'Name this relay',
  },
  ring: { cpu: 'CPU', mem: 'MEM', temp: 'TEMP', disk: 'DISK' },
  card: { org: 'Org', lastSeen: 'Last seen', publicIp: 'Public IP', backend: 'Backend', location: 'Location', uptime: 'Uptime' },
  dialog: {
    revokeHeading: 'Revoke {{name}}?',
    revokeMsg: 'This kill-switches the relay — its credentials are voided and it cannot reconnect.',
    removeHeading: 'Remove {{name}}?',
    removeMsg:
      'Permanently erases this relay from the registry — its record and org binding are deleted. The box must re-enroll to reappear. This cannot be undone.',
    unclaimHeading: 'Unclaim {{name}}?',
    unclaimMsg: 'Releases the relay from its org. Data is untouched (a metadata flip); the box becomes unowned until re-claimed.',
    gateway: 'relay',
  },
  claim: {
    codePlaceholder: 'Claim code (e.g. TG6KGVEL)',
    off_lan: 'Off your network — enter the code this relay displayed on enrollment to claim it remotely.',
    failed: 'Claim failed: {{message}}',
  },
  legend: { fleet: 'Fleet', online: 'Online', offline: 'Offline', unknown: 'Unknown', cloud: 'Private Server' },
  map: {
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    fitFleet: 'Frame the fleet',
    worldView: 'World view',
    dayNight: 'Day / night',
    minimap: 'Click to recenter',
    gateways: 'relays',
    clickToOpen: 'click to expand',
  },
  group: {
    status: 'Status',
    region: 'Region',
    channel: 'Channel',
    organization: 'Organization',
    unknownOrg: 'Unknown org',
    standard: 'Standard rollout',
  },
  msg: { loading: 'Loading fleet…', listFailed: 'ListFleet failed: {{message}}', noTransport: 'no transport' },
  view: {
    mapSub: '{{count}} relays',
    alertsTitle: 'Alerts',
    alertsSub: '{{crit}} critical · {{warn}} warnings',
    noAlerts: 'No active alerts. Fleet is healthy.',
    rolloutsTitle: 'Rollouts',
    rolloutsSub: 'Push & track relay updates across the fleet',
    releasesTitle: 'Releases',
    releasesSub: 'Release manifests & rollout channels',
    versionDist: 'Version distribution · backend',
    inTheField: 'In the field',
  },
  home: {
    greeting: 'Hi, {{name}}!',
    there: 'there',
    subtitle: 'How can I help you manage your fleet today?',
    askAnything: 'Ask AI anything about your fleet…',
    voice: 'Voice',
    empty: 'No relays enrolled yet.',
  },
  prompt: {
    q1: 'Are any of my relays offline?',
    q2: "How's my fleet's global health?",
    q3: 'Which relays need an update?',
    q4: 'Where are my relays located?',
  },
  alert: {
    critical: 'Critical',
    warning: 'Warning',
    revokedTitle: '{{name}} revoked',
    revokedBody: 'Credentials voided — cannot reconnect',
    offlineTitle: '{{name}} is offline',
    offlineBody: 'No heartbeat · last seen {{time}}',
    unknownTitle: '{{name}} status unknown',
    unknownBody: 'No recent presence signal',
  },
  stub: {
    rolloutTitle: 'Rollout campaigns',
    rolloutBody: 'Canary → staged → general rollout orchestration is wired up next.',
    releaseTitle: 'Release manifests',
    releaseBody: 'Draft → canary → general release management will live here. Wired up next.',
    noGwYet: 'No relays yet.',
    backendVersions: 'Backend versions',
    gateways: 'Relays',
  },
};

// French / Spanish / German / Japanese mirror en's shape.
const fr: typeof en = {
  tree: { overview: 'Aperçu', releases: 'Versions', map: 'Carte', alerts: 'Alertes', rollouts: 'Déploiements', people: 'Permissions' },
  people: {
    pick: 'Sélectionnez un membre pour voir ou modifier ses droits.',
    noMembers: 'Aucun membre dans cette organisation.',
    filter: 'Filtrer les permissions…',
    save: 'Enregistrer',
    saving: 'Enregistrement…',
    defaultAllowed: 'Par défaut — autorisé',
    defaultDenied: 'Par défaut — administrateurs',
    all: 'Toutes les surfaces',
    none: 'Révoqué',
    hintMemberDefault: 'Accordé aux membres sauf révocation',
    hintAdminOnly: 'Administrateurs uniquement sauf attribution',
    rowLabel: 'Permission {{key}}',
  },
  alerts: {
    critical: '{{n}} critiques',
    warnings: '{{n}} avertissements',
    nTotal: '{{n}} événements',
  },
  tab: { overview: 'Aperçu', hardware: 'Matériel', network: 'Réseau', software: 'Logiciel', security: 'Sécurité' },
  ids: {
    notRunning: 'IDS non actif sur ce relais',
    loss: 'Perte capture',
    threat: 'Menace',
    alerts: 'Alertes',
    severity: 'Crit · Élev · Moy',
    rules: 'Règles',
    blocks: 'Blocages',
    topSignatures: 'Top signatures',
    topSources: 'Top sources',
    protocols: 'Protocoles',
    activeBlocks: 'Blocages actifs',
    engineUp: 'Moteur actif',
    engineDown: 'Moteur arrêté',
    mode: { off: 'Désactivé', monitor: 'Surveillance', protect: 'Protection' },
  },
  server: {
    resources: 'Ressources',
    cpu: 'Processeur',
    memory: 'Mémoire',
    disk: 'Disque',
    restricted: 'Statistiques réservées aux administrateurs Vitesse',
    location: 'Emplacement',
    uptime: 'Disponibilité',
    load: 'Charge moy.',
    processes: 'Processus',
    model: 'Processeur',
    cores: 'Cœurs',
    os: 'OS',
    kernel: 'Noyau',
    arch: 'Architecture',
    memTotal: 'Mémoire',
    diskTotal: 'Disque',
  },
  m: {
    apiSuccess: 'Succès API',
    activeConns: 'Connexions',
    power: 'Puissance',
    diskUsed: 'Disque utilisé',
    diskFree: 'Disque libre',
    latency: 'Latence',
    egress: 'Sortie',
    errorRate: "Taux d'erreur",
    release: 'Version',
    attacks: 'Attaques',
    dataPoints: 'Points de données',
    packets: 'Paquets perdus',
    cloudLatency: 'Latence cloud',
    brokerLoad: 'Broker edge',
    threads: 'CPU libre',
    ips: 'Msg/s',
    disk: 'Disque',
    updateStatus: 'Mise à jour',
    itMac: 'MAC IT',
    downtime: 'Indispo',
    ingress: 'Entrée',
    traffic: 'Trafic',
    cloudBroker: 'Broker',
    flows: 'Flux',
    sessions: 'Sessions',
    pktsPerSec: 'Pqts/s',
    idsCpu: 'CPU IDS',
    idsMem: 'Mém IDS',
    memUsed: 'Mémoire utilisée',
    serverType: 'Type',
  },
  btn: {
    unclaim: 'Libérer',
    revoke: 'Révoquer',
    remove: 'Supprimer',
    rename: 'Renommer',
    save: 'Enregistrer',
    cancel: 'Annuler',
    close: 'Fermer',
    retry: 'réessayer',
    claim: 'Revendiquer',
  },
  hint: {
    unclaim: 'Détacher ce relais de son organisation',
    revoke: 'Coupe-circuit — annuler les identifiants, bloquer la reconnexion',
    remove: 'Effacer définitivement du registre',
    rename: 'Renommer ce relais',
    refresh: 'Actualiser la flotte',
    claim: "Revendiquer en saisissant le code affiché lors de l'enregistrement",
  },
  status: {
    online: 'En ligne',
    offline: 'Hors ligne',
    unknown: 'Inconnu',
    claimed: 'Revendiqué',
    unclaimed: 'Non revendiqué',
    revoked: 'Révoqué',
  },
  panel: {
    liveHealth: 'État en direct',
    quickFacts: 'Infos rapides',
    utilization: 'Utilisation',
    identifiers: 'Identifiants',
    addresses: 'Adresses',
    versions: 'Versions',
    ownerOrg: 'Organisation',
    location: 'Emplacement',
    coordinates: 'Coordonnées',
    lastSeen: 'Vu pour la dernière fois',
    firstSeen: 'Première apparition',
    connection: 'Connexion',
    tier: 'Niveau',
    uptime: 'Disponibilité',
    publicIp: 'IP publique',
    itIp: 'IP IT',
    otIp: 'IP OT',
    wifiIp: 'IP Wi-Fi',
    mac: 'Adresse MAC',
    otMac: 'Adresse MAC OT',
    gatewayId: 'ID relais',
    hostname: "Nom d'hôte",
    nodeKind: 'Type de nœud',
    claimStatus: 'Statut de revendication',
    backend: 'Backend',
    platform: 'Plateforme',
    updater: 'Mise à jour',
    docker: 'Docker',
    os: 'OS',
    unnamed: 'Relais sans nom',
    namePlaceholder: 'Nommer ce relais',
  },
  ring: { cpu: 'CPU', mem: 'MÉM', temp: 'TEMP', disk: 'DISQUE' },
  card: {
    org: 'Organisation',
    lastSeen: 'Vu',
    publicIp: 'IP publique',
    backend: 'Backend',
    location: 'Emplacement',
    uptime: 'Disponibilité',
  },
  dialog: {
    revokeHeading: 'Révoquer {{name}} ?',
    revokeMsg: 'Cela coupe le relais — ses identifiants sont annulés et il ne peut plus se reconnecter.',
    removeHeading: 'Supprimer {{name}} ?',
    removeMsg:
      "Efface définitivement ce relais du registre — son enregistrement et son lien d'organisation sont supprimés. Le boîtier doit se réinscrire pour réapparaître. Irréversible.",
    unclaimHeading: 'Libérer {{name}} ?',
    unclaimMsg:
      "Détache le relais de son organisation. Les données sont intactes (un changement de métadonnées) ; le boîtier devient sans propriétaire jusqu'à une nouvelle revendication.",
    gateway: 'relais',
  },
  claim: {
    codePlaceholder: 'Code de revendication (ex. TG6KGVEL)',
    off_lan:
      "Hors de votre réseau — saisissez le code affiché par ce relais lors de l'enregistrement pour le revendiquer à distance.",
    failed: 'Échec de la revendication : {{message}}',
  },
  legend: { fleet: 'Flotte', online: 'En ligne', offline: 'Hors ligne', unknown: 'Inconnu', cloud: 'Serveur privé' },
  map: {
    zoomIn: 'Zoom avant',
    zoomOut: 'Zoom arrière',
    fitFleet: 'Cadrer la flotte',
    worldView: 'Vue mondiale',
    dayNight: 'Jour / nuit',
    minimap: 'Cliquer pour recentrer',
    gateways: 'relais',
    clickToOpen: 'cliquer pour ouvrir',
  },
  group: {
    status: 'Statut',
    region: 'Région',
    channel: 'Canal',
    organization: 'Organisation',
    unknownOrg: 'Organisation inconnue',
    standard: 'Déploiement standard',
  },
  msg: { loading: 'Chargement de la flotte…', listFailed: 'Échec de ListFleet : {{message}}', noTransport: 'aucun transport' },
  view: {
    mapSub: '{{count}} relais',
    alertsTitle: 'Alertes',
    alertsSub: '{{crit}} critiques · {{warn}} avertissements',
    noAlerts: 'Aucune alerte active. La flotte est saine.',
    rolloutsTitle: 'Déploiements',
    rolloutsSub: 'Diffuser et suivre les mises à jour des relais',
    releasesTitle: 'Versions',
    releasesSub: 'Manifestes de version et canaux de déploiement',
    versionDist: 'Répartition des versions · backend',
    inTheField: 'Sur le terrain',
  },
  home: {
    greeting: 'Bonjour {{name}} !',
    there: 'vous',
    subtitle: 'Comment puis-je vous aider à gérer votre flotte aujourd’hui ?',
    askAnything: 'Posez une question sur votre flotte…',
    voice: 'Voix',
    empty: 'Aucun relais enregistré pour le moment.',
  },
  prompt: {
    q1: 'Certains de mes relais sont-ils hors ligne ?',
    q2: 'Comment se porte ma flotte au niveau mondial ?',
    q3: 'Quels relais nécessitent une mise à jour ?',
    q4: 'Où se trouvent mes relais ?',
  },
  alert: {
    critical: 'Critique',
    warning: 'Avertissement',
    revokedTitle: '{{name}} révoquée',
    revokedBody: 'Identifiants annulés — reconnexion impossible',
    offlineTitle: '{{name}} est hors ligne',
    offlineBody: 'Aucun battement · vue {{time}}',
    unknownTitle: 'Statut de {{name}} inconnu',
    unknownBody: 'Aucun signal de présence récent',
  },
  stub: {
    rolloutTitle: 'Campagnes de déploiement',
    rolloutBody: "L'orchestration canary → progressif → général sera ajoutée prochainement.",
    releaseTitle: 'Manifestes de version',
    releaseBody: 'La gestion des versions brouillon → canary → général arrivera ici. Prochainement.',
    noGwYet: 'Aucun relais pour le moment.',
    backendVersions: 'Versions backend',
    gateways: 'Relais',
  },
};

const es: typeof en = {
  tree: { overview: 'Resumen', releases: 'Versiones', map: 'Mapa', alerts: 'Alertas', rollouts: 'Despliegues', people: 'Permisos' },
  people: {
    pick: 'Seleccione un miembro para ver o cambiar lo que puede hacer.',
    noMembers: 'Aún no hay miembros en esta organización.',
    filter: 'Filtrar permisos…',
    save: 'Guardar',
    saving: 'Guardando…',
    defaultAllowed: 'Por defecto — permitido',
    defaultDenied: 'Por defecto — solo administradores',
    all: 'Todas las superficies',
    none: 'Revocado',
    hintMemberDefault: 'Los miembros lo tienen salvo revocación',
    hintAdminOnly: 'Solo administradores salvo concesión',
    rowLabel: 'Permiso {{key}}',
  },
  alerts: {
    critical: '{{n}} críticas',
    warnings: '{{n}} advertencias',
    nTotal: '{{n}} eventos',
  },
  tab: { overview: 'Resumen', hardware: 'Hardware', network: 'Red', software: 'Software', security: 'Seguridad' },
  ids: {
    notRunning: 'IDS no activo en este relé',
    loss: 'Pérdida captura',
    threat: 'Amenaza',
    alerts: 'Alertas',
    severity: 'Crít · Alta · Med',
    rules: 'Reglas',
    blocks: 'Bloqueos',
    topSignatures: 'Top firmas',
    topSources: 'Top orígenes',
    protocols: 'Protocolos',
    activeBlocks: 'Bloqueos activos',
    engineUp: 'Motor activo',
    engineDown: 'Motor parado',
    mode: { off: 'Desactivado', monitor: 'Monitor', protect: 'Protección' },
  },
  server: {
    resources: 'Recursos',
    cpu: 'CPU',
    memory: 'Memoria',
    disk: 'Disco',
    restricted: 'Estadísticas restringidas a administradores de Vitesse',
    location: 'Ubicación',
    uptime: 'Tiempo activo',
    load: 'Carga media',
    processes: 'Procesos',
    model: 'CPU',
    cores: 'Núcleos',
    os: 'SO',
    kernel: 'Kernel',
    arch: 'Arquitectura',
    memTotal: 'Memoria',
    diskTotal: 'Disco',
  },
  m: {
    apiSuccess: 'Éxito API',
    activeConns: 'Conexiones',
    power: 'Potencia',
    diskUsed: 'Disco usado',
    diskFree: 'Disco libre',
    latency: 'Latencia',
    egress: 'Salida',
    errorRate: 'Tasa de error',
    release: 'Versión',
    attacks: 'Ataques',
    dataPoints: 'Puntos de datos',
    packets: 'Paquetes perdidos',
    cloudLatency: 'Latencia nube',
    brokerLoad: 'Broker edge',
    threads: 'CPU libre',
    ips: 'Msg/s',
    disk: 'Disco',
    updateStatus: 'Actualización',
    itMac: 'MAC IT',
    downtime: 'Inactividad',
    ingress: 'Entrada',
    traffic: 'Tráfico',
    cloudBroker: 'Bróker',
    flows: 'Flujos',
    sessions: 'Sesiones',
    pktsPerSec: 'Pqts/s',
    idsCpu: 'CPU IDS',
    idsMem: 'Mem IDS',
    memUsed: 'Memoria usada',
    serverType: 'Tipo',
  },
  btn: {
    unclaim: 'Liberar',
    revoke: 'Revocar',
    remove: 'Eliminar',
    rename: 'Renombrar',
    save: 'Guardar',
    cancel: 'Cancelar',
    close: 'Cerrar',
    retry: 'reintentar',
    claim: 'Reclamar',
  },
  hint: {
    unclaim: 'Desvincular este relé de su organización',
    revoke: 'Interruptor de seguridad — anular credenciales, bloquear reconexión',
    remove: 'Borrar permanentemente del registro',
    rename: 'Renombrar este relé',
    refresh: 'Actualizar la flota',
    claim: 'Reclamar introduciendo el código mostrado al inscribirse',
  },
  status: {
    online: 'En línea',
    offline: 'Desconectado',
    unknown: 'Desconocido',
    claimed: 'Reclamado',
    unclaimed: 'Sin reclamar',
    revoked: 'Revocado',
  },
  panel: {
    liveHealth: 'Estado en vivo',
    quickFacts: 'Datos rápidos',
    utilization: 'Utilización',
    identifiers: 'Identificadores',
    addresses: 'Direcciones',
    versions: 'Versiones',
    ownerOrg: 'Organización',
    location: 'Ubicación',
    coordinates: 'Coordenadas',
    lastSeen: 'Visto por última vez',
    firstSeen: 'Visto por primera vez',
    connection: 'Conexión',
    tier: 'Nivel',
    uptime: 'Tiempo activo',
    publicIp: 'IP pública',
    itIp: 'IP IT',
    otIp: 'IP OT',
    wifiIp: 'IP Wi-Fi',
    mac: 'Dirección MAC',
    otMac: 'Dirección MAC OT',
    gatewayId: 'ID de relé',
    hostname: 'Nombre de host',
    nodeKind: 'Tipo de nodo',
    claimStatus: 'Estado de reclamación',
    backend: 'Backend',
    platform: 'Plataforma',
    updater: 'Actualizador',
    docker: 'Docker',
    os: 'SO',
    unnamed: 'Relé sin nombre',
    namePlaceholder: 'Nombrar este relé',
  },
  ring: { cpu: 'CPU', mem: 'MEM', temp: 'TEMP', disk: 'DISK' },
  card: {
    org: 'Organización',
    lastSeen: 'Visto',
    publicIp: 'IP pública',
    backend: 'Backend',
    location: 'Ubicación',
    uptime: 'Tiempo activo',
  },
  dialog: {
    revokeHeading: '¿Revocar {{name}}?',
    revokeMsg: 'Esto desactiva el relé — sus credenciales se anulan y no puede volver a conectarse.',
    removeHeading: '¿Eliminar {{name}}?',
    removeMsg:
      'Borra permanentemente este relé del registro — se eliminan su registro y su vínculo de organización. El equipo debe volver a inscribirse para reaparecer. No se puede deshacer.',
    unclaimHeading: '¿Liberar {{name}}?',
    unclaimMsg:
      'Libera el relé de su organización. Los datos quedan intactos (un cambio de metadatos); el equipo queda sin dueño hasta que se reclame de nuevo.',
    gateway: 'relé',
  },
  claim: {
    codePlaceholder: 'Código de reclamación (ej. TG6KGVEL)',
    off_lan: 'Fuera de su red — introduzca el código que mostró este relé al inscribirse para reclamarlo de forma remota.',
    failed: 'Error al reclamar: {{message}}',
  },
  legend: { fleet: 'Flota', online: 'En línea', offline: 'Desconectado', unknown: 'Desconocido', cloud: 'Servidor privado' },
  map: {
    zoomIn: 'Acercar',
    zoomOut: 'Alejar',
    fitFleet: 'Encuadrar la flota',
    worldView: 'Vista mundial',
    dayNight: 'Día / noche',
    minimap: 'Clic para recentrar',
    gateways: 'relés',
    clickToOpen: 'clic para abrir',
  },
  group: {
    status: 'Estado',
    region: 'Región',
    channel: 'Canal',
    organization: 'Organización',
    unknownOrg: 'Organización desconocida',
    standard: 'Despliegue estándar',
  },
  msg: { loading: 'Cargando la flota…', listFailed: 'ListFleet falló: {{message}}', noTransport: 'sin transporte' },
  view: {
    mapSub: '{{count}} relés',
    alertsTitle: 'Alertas',
    alertsSub: '{{crit}} críticas · {{warn}} advertencias',
    noAlerts: 'No hay alertas activas. La flota está sana.',
    rolloutsTitle: 'Despliegues',
    rolloutsSub: 'Enviar y seguir actualizaciones de relés',
    releasesTitle: 'Versiones',
    releasesSub: 'Manifiestos de versión y canales de despliegue',
    versionDist: 'Distribución de versiones · backend',
    inTheField: 'En el campo',
  },
  home: {
    greeting: '¡Hola, {{name}}!',
    there: 'hola',
    subtitle: '¿Cómo puedo ayudarte a gestionar tu flota hoy?',
    askAnything: 'Pregunta cualquier cosa sobre tu flota…',
    voice: 'Voz',
    empty: 'Aún no hay relés registrados.',
  },
  prompt: {
    q1: '¿Alguno de mis relés está desconectado?',
    q2: '¿Cómo está la salud global de mi flota?',
    q3: '¿Qué relés necesitan actualización?',
    q4: '¿Dónde están mis relés?',
  },
  alert: {
    critical: 'Crítico',
    warning: 'Advertencia',
    revokedTitle: '{{name}} revocada',
    revokedBody: 'Credenciales anuladas — no puede reconectar',
    offlineTitle: '{{name}} está desconectada',
    offlineBody: 'Sin latido · vista {{time}}',
    unknownTitle: 'Estado de {{name}} desconocido',
    unknownBody: 'Sin señal de presencia reciente',
  },
  stub: {
    rolloutTitle: 'Campañas de despliegue',
    rolloutBody: 'La orquestación canary → escalonado → general se añadirá próximamente.',
    releaseTitle: 'Manifiestos de versión',
    releaseBody: 'La gestión de versiones borrador → canary → general estará aquí. Próximamente.',
    noGwYet: 'Aún no hay relés.',
    backendVersions: 'Versiones backend',
    gateways: 'Relés',
  },
};

const de: typeof en = {
  tree: { overview: 'Übersicht', releases: 'Releases', map: 'Karte', alerts: 'Warnungen', rollouts: 'Rollouts', people: 'Berechtigungen' },
  people: {
    pick: 'Wählen Sie ein Mitglied, um dessen Rechte zu sehen oder zu ändern.',
    noMembers: 'Noch keine Mitglieder in dieser Organisation.',
    filter: 'Berechtigungen filtern…',
    save: 'Speichern',
    saving: 'Wird gespeichert…',
    defaultAllowed: 'Standard — erlaubt',
    defaultDenied: 'Standard — nur Administratoren',
    all: 'Alle Oberflächen',
    none: 'Entzogen',
    hintMemberDefault: 'Mitglieder haben dies, sofern nicht entzogen',
    hintAdminOnly: 'Nur Administratoren, sofern nicht gewährt',
    rowLabel: 'Berechtigung {{key}}',
  },
  alerts: {
    critical: '{{n}} kritisch',
    warnings: '{{n}} Warnungen',
    nTotal: '{{n}} Ereignisse',
  },
  tab: { overview: 'Übersicht', hardware: 'Hardware', network: 'Netzwerk', software: 'Software', security: 'Sicherheit' },
  ids: {
    notRunning: 'IDS auf diesem Relay nicht aktiv',
    loss: 'Erfassungsverlust',
    threat: 'Bedrohung',
    alerts: 'Alarme',
    severity: 'Krit · Hoch · Mit',
    rules: 'Regeln',
    blocks: 'Sperren',
    topSignatures: 'Top-Signaturen',
    topSources: 'Top-Quellen',
    protocols: 'Protokolle',
    activeBlocks: 'Aktive Sperren',
    engineUp: 'Engine aktiv',
    engineDown: 'Engine gestoppt',
    mode: { off: 'Aus', monitor: 'Überwachung', protect: 'Schutz' },
  },
  server: {
    resources: 'Ressourcen',
    cpu: 'CPU',
    memory: 'Arbeitsspeicher',
    disk: 'Festplatte',
    restricted: 'Statistiken nur für Vitesse-Admins',
    location: 'Standort',
    uptime: 'Betriebszeit',
    load: 'Last (Ø)',
    processes: 'Prozesse',
    model: 'CPU',
    cores: 'Kerne',
    os: 'Betriebssystem',
    kernel: 'Kernel',
    arch: 'Architektur',
    memTotal: 'Arbeitsspeicher',
    diskTotal: 'Festplatte',
  },
  m: {
    apiSuccess: 'API-Erfolg',
    activeConns: 'Verbindungen',
    power: 'Leistung',
    diskUsed: 'Belegt',
    diskFree: 'Frei',
    latency: 'Latenz',
    egress: 'Ausgang',
    errorRate: 'Fehlerrate',
    release: 'Version',
    attacks: 'Angriffe',
    dataPoints: 'Datenpunkte',
    packets: 'Paketverlust',
    cloudLatency: 'Cloud-Latenz',
    brokerLoad: 'Edge-Broker',
    threads: 'CPU frei',
    ips: 'Msg/s',
    disk: 'Disk',
    updateStatus: 'Update',
    itMac: 'IT-MAC',
    downtime: 'Ausfall',
    ingress: 'Eingang',
    traffic: 'Traffic',
    cloudBroker: 'Broker',
    flows: 'Flows',
    sessions: 'Sitzungen',
    pktsPerSec: 'Pkts/s',
    idsCpu: 'IDS-CPU',
    idsMem: 'IDS-RAM',
    memUsed: 'RAM belegt',
    serverType: 'Typ',
  },
  btn: {
    unclaim: 'Freigeben',
    revoke: 'Widerrufen',
    remove: 'Entfernen',
    rename: 'Umbenennen',
    save: 'Speichern',
    cancel: 'Abbrechen',
    close: 'Schließen',
    retry: 'erneut',
    claim: 'Beanspruchen',
  },
  hint: {
    unclaim: 'Dieses Relay von seiner Organisation lösen',
    revoke: 'Not-Aus — Anmeldedaten ungültig machen, Wiederverbindung sperren',
    remove: 'Dauerhaft aus der Registry löschen',
    rename: 'Dieses Relay umbenennen',
    refresh: 'Flotte aktualisieren',
    claim: 'Mit dem bei der Registrierung angezeigten Code beanspruchen',
  },
  status: {
    online: 'Online',
    offline: 'Offline',
    unknown: 'Unbekannt',
    claimed: 'Beansprucht',
    unclaimed: 'Nicht beansprucht',
    revoked: 'Widerrufen',
  },
  panel: {
    liveHealth: 'Live-Zustand',
    quickFacts: 'Schnellinfos',
    utilization: 'Auslastung',
    identifiers: 'Bezeichner',
    addresses: 'Adressen',
    versions: 'Versionen',
    ownerOrg: 'Organisation',
    location: 'Standort',
    coordinates: 'Koordinaten',
    lastSeen: 'Zuletzt gesehen',
    firstSeen: 'Zuerst gesehen',
    connection: 'Verbindung',
    tier: 'Stufe',
    uptime: 'Laufzeit',
    publicIp: 'Öffentliche IP',
    itIp: 'IT-IP',
    otIp: 'OT-IP',
    wifiIp: 'WLAN-IP',
    mac: 'MAC-Adresse',
    otMac: 'OT-MAC-Adresse',
    gatewayId: 'Relay-ID',
    hostname: 'Hostname',
    nodeKind: 'Knotentyp',
    claimStatus: 'Beanspruchungsstatus',
    backend: 'Backend',
    platform: 'Plattform',
    updater: 'Updater',
    docker: 'Docker',
    os: 'OS',
    unnamed: 'Unbenanntes Relay',
    namePlaceholder: 'Dieses Relay benennen',
  },
  ring: { cpu: 'CPU', mem: 'MEM', temp: 'TEMP', disk: 'DISK' },
  card: {
    org: 'Organisation',
    lastSeen: 'Gesehen',
    publicIp: 'Öffentliche IP',
    backend: 'Backend',
    location: 'Standort',
    uptime: 'Laufzeit',
  },
  dialog: {
    revokeHeading: '{{name}} widerrufen?',
    revokeMsg: 'Dies schaltet das Relay ab — seine Anmeldedaten werden ungültig und es kann sich nicht mehr verbinden.',
    removeHeading: '{{name}} entfernen?',
    removeMsg:
      'Löscht dieses Relay dauerhaft aus der Registry — Datensatz und Org-Bindung werden gelöscht. Das Gerät muss sich neu registrieren, um wieder zu erscheinen. Nicht umkehrbar.',
    unclaimHeading: '{{name}} freigeben?',
    unclaimMsg:
      'Löst das Relay von seiner Organisation. Daten bleiben unberührt (ein Metadaten-Wechsel); das Gerät ist ohne Eigentümer, bis es neu beansprucht wird.',
    gateway: 'Relay',
  },
  claim: {
    codePlaceholder: 'Beanspruchungscode (z. B. TG6KGVEL)',
    off_lan:
      'Außerhalb Ihres Netzwerks — geben Sie den bei der Registrierung angezeigten Code ein, um dieses Relay aus der Ferne zu beanspruchen.',
    failed: 'Beanspruchung fehlgeschlagen: {{message}}',
  },
  legend: { fleet: 'Flotte', online: 'Online', offline: 'Offline', unknown: 'Unbekannt', cloud: 'Privater Server' },
  map: {
    zoomIn: 'Vergrößern',
    zoomOut: 'Verkleinern',
    fitFleet: 'Flotte einrahmen',
    worldView: 'Weltansicht',
    dayNight: 'Tag / Nacht',
    minimap: 'Klicken zum Zentrieren',
    gateways: 'Relays',
    clickToOpen: 'zum Öffnen klicken',
  },
  group: {
    status: 'Status',
    region: 'Region',
    channel: 'Kanal',
    organization: 'Organisation',
    unknownOrg: 'Unbekannte Organisation',
    standard: 'Standard-Rollout',
  },
  msg: { loading: 'Flotte wird geladen…', listFailed: 'ListFleet fehlgeschlagen: {{message}}', noTransport: 'kein Transport' },
  view: {
    mapSub: '{{count}} Relays',
    alertsTitle: 'Warnungen',
    alertsSub: '{{crit}} kritisch · {{warn}} Warnungen',
    noAlerts: 'Keine aktiven Warnungen. Die Flotte ist gesund.',
    rolloutsTitle: 'Rollouts',
    rolloutsSub: 'Relay-Updates flottenweit verteilen & verfolgen',
    releasesTitle: 'Releases',
    releasesSub: 'Release-Manifeste & Rollout-Kanäle',
    versionDist: 'Versionsverteilung · Backend',
    inTheField: 'Im Einsatz',
  },
  home: {
    greeting: 'Hallo, {{name}}!',
    there: 'da',
    subtitle: 'Wie kann ich Ihnen heute bei Ihrer Flotte helfen?',
    askAnything: 'Fragen Sie die KI alles über Ihre Flotte…',
    voice: 'Stimme',
    empty: 'Noch keine Relays registriert.',
  },
  prompt: {
    q1: 'Sind einige meiner Relays offline?',
    q2: 'Wie ist der globale Zustand meiner Flotte?',
    q3: 'Welche Relays brauchen ein Update?',
    q4: 'Wo befinden sich meine Relays?',
  },
  alert: {
    critical: 'Kritisch',
    warning: 'Warnung',
    revokedTitle: '{{name}} widerrufen',
    revokedBody: 'Anmeldedaten ungültig — keine Wiederverbindung möglich',
    offlineTitle: '{{name}} ist offline',
    offlineBody: 'Kein Herzschlag · zuletzt {{time}}',
    unknownTitle: 'Status von {{name}} unbekannt',
    unknownBody: 'Kein aktuelles Präsenzsignal',
  },
  stub: {
    rolloutTitle: 'Rollout-Kampagnen',
    rolloutBody: 'Canary → gestaffelte → allgemeine Rollout-Orchestrierung folgt als Nächstes.',
    releaseTitle: 'Release-Manifeste',
    releaseBody: 'Entwurf → Canary → allgemeine Release-Verwaltung kommt hierher. Folgt als Nächstes.',
    noGwYet: 'Noch keine Relays.',
    backendVersions: 'Backend-Versionen',
    gateways: 'Relays',
  },
};

const ja: typeof en = {
  tree: { overview: '概要', releases: 'リリース', map: 'マップ', alerts: 'アラート', rollouts: 'ロールアウト', people: '権限' },
  people: {
    pick: 'メンバーを選択すると、権限を確認・変更できます。',
    noMembers: 'この組織にはまだメンバーがいません。',
    filter: '権限を絞り込む…',
    save: '保存',
    saving: '保存中…',
    defaultAllowed: '既定 — 許可',
    defaultDenied: '既定 — 管理者のみ',
    all: 'すべての画面',
    none: '取り消し',
    hintMemberDefault: '取り消さない限りメンバーに付与',
    hintAdminOnly: '付与しない限り管理者のみ',
    rowLabel: '権限 {{key}}',
  },
  alerts: {
    critical: '緊急 {{n}}',
    warnings: '警告 {{n}}',
    nTotal: '{{n}} 件のイベント',
  },
  tab: { overview: '概要', hardware: 'ハードウェア', network: 'ネットワーク', software: 'ソフトウェア', security: 'セキュリティ' },
  ids: {
    notRunning: 'このリレーでIDSは未稼働',
    loss: 'キャプチャ損失',
    threat: '脅威',
    alerts: 'アラート',
    severity: '重大 · 高 · 中',
    rules: 'ルール',
    blocks: 'ブロック',
    topSignatures: '上位シグネチャ',
    topSources: '上位送信元',
    protocols: 'プロトコル',
    activeBlocks: '有効なブロック',
    engineUp: 'エンジン稼働',
    engineDown: 'エンジン停止',
    mode: { off: 'オフ', monitor: '監視', protect: '保護' },
  },
  server: {
    resources: 'リソース',
    cpu: 'CPU',
    memory: 'メモリ',
    disk: 'ディスク',
    restricted: '統計はVitesse管理者のみに表示されます',
    location: '場所',
    uptime: '稼働時間',
    load: '負荷平均',
    processes: 'プロセス',
    model: 'CPU',
    cores: 'コア',
    os: 'OS',
    kernel: 'カーネル',
    arch: 'アーキテクチャ',
    memTotal: 'メモリ',
    diskTotal: 'ディスク',
  },
  m: {
    apiSuccess: 'API成功率',
    activeConns: '接続数',
    power: '消費電力',
    diskUsed: 'ディスク使用',
    diskFree: 'ディスク空き',
    latency: 'レイテンシ',
    egress: '送信量',
    errorRate: 'エラー率',
    release: 'バージョン',
    attacks: '攻撃',
    dataPoints: 'データポイント',
    packets: 'パケット損失',
    cloudLatency: 'クラウド遅延',
    brokerLoad: 'エッジブローカー',
    threads: 'CPU空き',
    ips: 'メッセージ/秒',
    disk: 'ディスク',
    updateStatus: '更新',
    itMac: 'IT MAC',
    downtime: 'ダウンタイム',
    ingress: '受信',
    traffic: 'トラフィック',
    cloudBroker: 'ブローカー',
    flows: 'フロー',
    sessions: 'セッション',
    pktsPerSec: 'パケット/秒',
    idsCpu: 'IDS CPU',
    idsMem: 'IDSメモリ',
    memUsed: 'メモリ使用',
    serverType: 'タイプ',
  },
  btn: {
    unclaim: '解除',
    revoke: '失効',
    remove: '削除',
    rename: '名前変更',
    save: '保存',
    cancel: 'キャンセル',
    close: '閉じる',
    retry: '再試行',
    claim: '登録',
  },
  hint: {
    unclaim: 'このリレーを組織から切り離す',
    revoke: 'キルスイッチ — 資格情報を無効化し再接続を遮断',
    remove: 'レジストリから完全に削除',
    rename: 'このリレーの名前を変更',
    refresh: 'フリートを更新',
    claim: '登録時に表示されたコードで登録する',
  },
  status: { online: 'オンライン', offline: 'オフライン', unknown: '不明', claimed: '登録済み', unclaimed: '未登録', revoked: '失効' },
  panel: {
    liveHealth: 'ライブ状態',
    quickFacts: '概要情報',
    utilization: '使用率',
    identifiers: '識別子',
    addresses: 'アドレス',
    versions: 'バージョン',
    ownerOrg: '所有組織',
    location: '所在地',
    coordinates: '座標',
    lastSeen: '最終確認',
    firstSeen: '初回確認',
    connection: '接続',
    tier: 'ティア',
    uptime: '稼働時間',
    publicIp: 'パブリックIP',
    itIp: 'IT IP',
    otIp: 'OT IP',
    wifiIp: 'Wi-Fi IP',
    mac: 'MACアドレス',
    otMac: 'OT MACアドレス',
    gatewayId: 'リレーID',
    hostname: 'ホスト名',
    nodeKind: 'ノード種別',
    claimStatus: '登録状態',
    backend: 'バックエンド',
    platform: 'プラットフォーム',
    updater: 'アップデーター',
    docker: 'Docker',
    os: 'OS',
    unnamed: '名称未設定のリレー',
    namePlaceholder: 'このリレーに名前を付ける',
  },
  ring: { cpu: 'CPU', mem: 'メモリ', temp: '温度', disk: 'ディスク' },
  card: { org: '組織', lastSeen: '確認', publicIp: 'パブリックIP', backend: 'バックエンド', location: '所在地', uptime: '稼働時間' },
  dialog: {
    revokeHeading: '{{name}} を失効しますか？',
    revokeMsg: 'リレーを停止します — 資格情報が無効になり、再接続できなくなります。',
    removeHeading: '{{name}} を削除しますか？',
    removeMsg:
      'このリレーをレジストリから完全に削除します — レコードと組織の紐付けが削除されます。再表示には再登録が必要です。元に戻せません。',
    unclaimHeading: '{{name}} を解除しますか？',
    unclaimMsg: '組織からリレーを解放します。データはそのまま（メタデータの切替）で、再登録まで所有者なしになります。',
    gateway: 'リレー',
  },
  claim: {
    codePlaceholder: '登録コード（例：TG6KGVEL）',
    off_lan: 'ネットワーク外のリレーです — 登録時に表示されたコードを入力してリモートで登録してください。',
    failed: '登録に失敗しました：{{message}}',
  },
  legend: { fleet: 'フリート', online: 'オンライン', offline: 'オフライン', unknown: '不明', cloud: 'プライベートサーバー' },
  map: {
    zoomIn: 'ズームイン',
    zoomOut: 'ズームアウト',
    fitFleet: 'フリートに合わせる',
    worldView: '世界表示',
    dayNight: '昼 / 夜',
    minimap: 'クリックして再センタリング',
    gateways: 'リレー',
    clickToOpen: 'クリックで展開',
  },
  group: {
    status: 'ステータス',
    region: '地域',
    channel: 'チャネル',
    organization: '組織',
    unknownOrg: '不明な組織',
    standard: '標準ロールアウト',
  },
  msg: { loading: 'フリートを読み込み中…', listFailed: 'ListFleet に失敗しました: {{message}}', noTransport: 'トランスポートなし' },
  view: {
    mapSub: '{{count}} 台のリレー',
    alertsTitle: 'アラート',
    alertsSub: '重大 {{crit}} 件 · 警告 {{warn}} 件',
    noAlerts: 'アクティブなアラートはありません。フリートは正常です。',
    rolloutsTitle: 'ロールアウト',
    rolloutsSub: 'フリート全体のリレー更新を配信・追跡',
    releasesTitle: 'リリース',
    releasesSub: 'リリースマニフェストとロールアウトチャネル',
    versionDist: 'バージョン分布 · バックエンド',
    inTheField: '稼働中',
  },
  home: {
    greeting: 'こんにちは、{{name}}さん！',
    there: 'ようこそ',
    subtitle: '今日はフリートの管理をどのようにお手伝いしましょうか？',
    askAnything: 'フリートについてAIに何でも質問…',
    voice: '音声',
    empty: 'まだリレーが登録されていません。',
  },
  prompt: {
    q1: 'オフラインのリレーはありますか？',
    q2: 'フリート全体の健全性は？',
    q3: '更新が必要なリレーは？',
    q4: 'リレーはどこにありますか？',
  },
  alert: {
    critical: '重大',
    warning: '警告',
    revokedTitle: '{{name}} を失効',
    revokedBody: '資格情報が無効 — 再接続できません',
    offlineTitle: '{{name}} はオフライン',
    offlineBody: 'ハートビートなし · 最終確認 {{time}}',
    unknownTitle: '{{name}} の状態が不明',
    unknownBody: '最近の存在シグナルがありません',
  },
  stub: {
    rolloutTitle: 'ロールアウトキャンペーン',
    rolloutBody: 'カナリア → 段階的 → 全体ロールアウトのオーケストレーションは近日対応。',
    releaseTitle: 'リリースマニフェスト',
    releaseBody: 'ドラフト → カナリア → 全体のリリース管理がここに入ります。近日対応。',
    noGwYet: 'まだリレーがありません。',
    backendVersions: 'バックエンドバージョン',
    gateways: 'リレー',
  },
};

let registered = false;
/** Register the `fleet` namespace onto base-ui's i18next singleton. Call AFTER
 *  initI18n(). Idempotent. */
export function registerFleetI18n(): void {
  if (registered) return;
  registered = true;
  i18next.addResourceBundle('en', 'fleet', en, true, true);
  i18next.addResourceBundle('fr', 'fleet', fr, true, true);
  i18next.addResourceBundle('es', 'fleet', es, true, true);
  i18next.addResourceBundle('de', 'fleet', de, true, true);
  i18next.addResourceBundle('ja', 'fleet', ja, true, true);
}

/** Translate a `fleet`-namespace key (e.g. 'btn.unclaim', 'dialog.removeHeading').
 *  Loosely typed on purpose — the namespace is registered at runtime, not in
 *  base-ui's compile-time 'translation' resources. */
export function ft(key: string, opts?: Record<string, unknown>): string {
  return (i18next.t as unknown as (k: string, o?: object) => string)(key, { ns: 'fleet', ...opts });
}

/** Lit reactive controller: re-render the host whenever the language changes, so
 *  translated text updates live. Add `new LocaleController(this)` in any element
 *  that renders ft(...) text. */
export class LocaleController implements ReactiveController {
  private unsub?: () => void;
  constructor(private readonly host: ReactiveControllerHost) {
    host.addController(this);
  }
  hostConnected(): void {
    this.unsub = onLocaleChange(() => this.host.requestUpdate());
  }
  hostDisconnected(): void {
    this.unsub?.();
  }
}
